import React from "react";
import { useTranslation } from "react-i18next";
import { QueryError } from "@/components/shared/query-error";
import { DataTablePagination } from "@/components/table/data-table-pagination";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchInput } from "@/components/ui/search-input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { DividerRow, SortIcon } from "./meter-table-parts";
import type { MeterTableProps } from "./meter-table-types";

export { usePagination } from "./meter-table-parts";
export type { Column } from "./meter-table-types";

export function MeterTable<T extends Record<string, unknown>>({
	data,
	columns,
	loading,
	isError,
	onRetry,
	page,
	pageSize = 20,
	onPageChange,
	onPageSizeChange,
	search = "",
	onSearchChange,
	searchPlaceholder,
	ordering,
	onOrderingChange,
	onRowClick,
	headerActions,
	selectionBar,
	rowKeyField = "serial_number",
	rowKey,
	selectedKeys,
	onSelectKey,
	onSelectAll,
	isAllSelected,
	isIndeterminate,
}: MeterTableProps<T>) {
	const { t } = useTranslation();

	const selectable = selectedKeys !== undefined;
	const colCount = columns.length + (selectable ? 1 : 0);

	function handleSort(sortField: string) {
		if (!onOrderingChange) return;
		if (ordering === sortField) onOrderingChange(`-${sortField}`);
		else onOrderingChange(sortField);
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-3">
			{(onSearchChange !== undefined || headerActions) && (
				<div className="flex flex-wrap items-center gap-2">
					{onSearchChange !== undefined && (
						<SearchInput
							wrapperClassName="w-full sm:w-auto"
							className="w-full sm:w-72 md:w-96"
							placeholder={
								searchPlaceholder ?? t("meterTable.searchPlaceholder")
							}
							value={search}
							onValueChange={onSearchChange}
						/>
					)}
					{headerActions && (
						<div
							className={onSearchChange !== undefined ? "ml-auto" : "flex-1"}
						>
							{headerActions}
						</div>
					)}
				</div>
			)}

			<div className="relative min-h-0 flex-1">
				{selectionBar !== undefined && (
					<div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-10 overflow-hidden rounded-t-md">
						{selectionBar}
					</div>
				)}
				<div className="h-full min-h-0 overflow-auto rounded-md border [&_[data-slot=table-container]]:overflow-x-visible">
					<Table>
						<TableHeader className="sticky top-0 z-10">
							<TableRow>
								{selectable && (
									<TableHead className="w-10">
										<Checkbox
											checked={
												isAllSelected
													? true
													: isIndeterminate
														? "indeterminate"
														: false
											}
											onCheckedChange={onSelectAll}
											aria-label={t("meterTable.selectAll")}
										/>
									</TableHead>
								)}
								{columns.map((col) => (
									<TableHead
										key={String(col.key)}
										onClick={
											col.sortField
												? () => handleSort(col.sortField!)
												: undefined
										}
										className={
											[
												col.sortField ? "cursor-pointer select-none" : "",
												col.key === "_actions" ? "w-14 text-right" : "",
											]
												.filter(Boolean)
												.join(" ") || undefined
										}
									>
										<span className="flex items-center gap-1.5">
											{col.label}
											{col.sortField && (
												<SortIcon field={col.sortField} ordering={ordering} />
											)}
										</span>
									</TableHead>
								))}
							</TableRow>
						</TableHeader>
						<TableBody>
							{loading ? (
								<>
									{Array.from({ length: 5 }).map((_, i) => (
										<React.Fragment key={i}>
											<TableRow>
												{selectable && <TableCell className="w-10" />}
												{columns.map((col) => (
													<TableCell key={String(col.key)}>
														<Skeleton className="h-4 w-full" />
													</TableCell>
												))}
											</TableRow>
											{i < 4 && <DividerRow colSpan={colCount} />}
										</React.Fragment>
									))}
								</>
							) : isError ? (
								<TableRow>
									<TableCell colSpan={colCount} className="py-8">
										<QueryError onRetry={onRetry} />
									</TableCell>
								</TableRow>
							) : data?.results?.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={colCount}
										className="text-muted-foreground py-8 text-center"
									>
										{t("meterTable.noData")}
									</TableCell>
								</TableRow>
							) : (
								<>
									{data?.results?.map((row, i) => {
										const sn = selectable
											? String(
													(row as Record<string, unknown>)[rowKeyField] ?? "",
												)
											: "";
										const computedRowKey = rowKey
											? rowKey(row)
											: String(
													(row as Record<string, unknown>)[rowKeyField] ?? i,
												);
										const isSelected = selectable && selectedKeys!.includes(sn);
										return (
											<React.Fragment key={computedRowKey}>
												<TableRow
													onClick={
														onRowClick ? () => onRowClick(row) : undefined
													}
													className={onRowClick ? "cursor-pointer" : undefined}
												>
													{selectable && (
														<TableCell
															className="w-10"
															onClick={(e) => {
																e.stopPropagation();
																onSelectKey?.(sn);
															}}
														>
															<Checkbox
																checked={isSelected}
																aria-label={`${t("meterTable.selectAll")} ${sn}`}
															/>
														</TableCell>
													)}
													{columns.map((col) => (
														<TableCell
															key={String(col.key)}
															className={
																col.key === "_actions"
																	? "w-14 text-right"
																	: undefined
															}
														>
															{col.render
																? col.render(row)
																: col.key === "_details" ||
																		col.key === "_actions"
																	? null
																	: String(
																			(row as Record<string, unknown>)[
																				col.key as string
																			] ?? "—",
																		)}
														</TableCell>
													))}
												</TableRow>
												{data.results[i + 1] !== undefined && (
													<DividerRow colSpan={colCount} />
												)}
											</React.Fragment>
										);
									})}
								</>
							)}
						</TableBody>
					</Table>
				</div>
			</div>

			{data && onPageSizeChange && onPageChange && (
				<DataTablePagination
					page={page ?? 1}
					pageSize={pageSize}
					count={data.count}
					onPageChange={onPageChange}
					onPageSizeChange={onPageSizeChange}
					hasPrevious={!!data.previous}
					hasNext={!!data.next}
				/>
			)}
		</div>
	);
}
